import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { FlexDiv } from '../design-system/containers';
import DividerWithContent from '../divider-with-content';
import ConversationItem from './conversation-item';
import { QuestionHeading } from './question-heading';

export const Conversation = observer(({ ui, handleChange, value, answer }) => {
    const [expandedConversationItems, expandConversationItemsGroup] = useState(
        []
    );

    const generateDisplayArray = () => {
        /** If we are not showing answers, we are showing the question and should show all conversation options */
        if (!answer) return ui.options;

        let display = [];
        const relevantConversationItems = {
            ...answer.correctAnswer,
            ...answer.userAnswer,
        };

        let groupOfHiddenConversationItems = [];
        ui.options.map((option, i) => {
            /** A 'select_none' option, that is not relevant, should not be part of the answer shown to the user */
            if (
                !relevantConversationItems[option.value] &&
                option.value === 'select_none'
            ) {
                return false;
            }
            /** If it is neither a relevant conversation item nor in a group we have expanded, we hide it (add it to a group)*/
            if (
                !relevantConversationItems[option.value] &&
                expandedConversationItems.indexOf(option.value) < 0
            ) {
                groupOfHiddenConversationItems.push(option);
            } else {
                if (groupOfHiddenConversationItems.length > 0) {
                    display.push(groupOfHiddenConversationItems);
                    groupOfHiddenConversationItems = [];
                }
                display.push(option);
            }
        });
        /** At the end we push the last group to our display array, if any */
        if (groupOfHiddenConversationItems.length > 0) {
            display.push(groupOfHiddenConversationItems);
        }

        return display;
    };

    useEffect(() => {
        expandConversationItemsGroup([]);
    }, [ui]);

    const data = useMemo(
        () => generateDisplayArray(),
        [expandedConversationItems.length, ui, answer]
    );

    const { t } = useTranslation('assessment');

    const onChange = (
        event,
        boxVal,
        shouldOverrideAllOthers = false,
        doOverrideValue = null
    ) => {
        if (answer) return;
        let val = [...(value || [])];

        if (event.target.checked) {
            if (!val.includes(boxVal)) {
                /** Normally we just add this new block value to existing values, but if 'There were no potentially
                 * sensitive information being exchanged.' was selected we override all others with the given value. */
                if (shouldOverrideAllOthers) {
                    val = [boxVal];
                } else {
                    val.push(boxVal);
                    /** ...and for every other option selected, we remove the 'select_all' checkmark/value*/
                    if (doOverrideValue) {
                        val = val.filter((x) => x !== doOverrideValue);
                    }
                }
            }
        } else {
            val = val.filter((x) => x !== boxVal);
        }
        handleChange(val, null, null, true);
    };


    const onExpand = (itemsToExpand) => {
        expandConversationItemsGroup(
            expandedConversationItems.concat(itemsToExpand)
        );
    };

    return (
        <div className={'assessment'}>
            <div className={'assessment-question'}>
                <QuestionHeading ui={ui} />
                <FlexDiv column gap={8}>
                    <FlexDiv column gap={8}>
                        {data.map((node, i) => {
                            return Array.isArray(node) ? (
                                <DividerWithContent
                                    key={'divider-' + i}
                                    items={node}
                                    onClick={onExpand}
                                />
                            ) : (
                                <ConversationItem
                                    onChange={onChange}
                                    groupValue={value}
                                    key={'item-' + i}
                                    value={node.value}
                                    answer={answer}
                                    ui={ui}
                                    node={node}
                                />
                            );
                        })}
                    </FlexDiv>
                </FlexDiv>
            </div>
        </div>
    );
});
